"use client";

import { useEffect, useRef, useState, type ReactNode } from "react";
import { Search, ChevronDown, SlidersHorizontal, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { PERIODES_LISTE, libelleDePeriode, type PeriodeListe } from "@/lib/liste-documents";

/**
 * LA BARRE COMPACTE : recherche, période, filtres.
 *
 * Une seule ligne. La période propose l'année en cours d'abord — c'est le
 * filtre à l'ouverture, et il change tout seul le 1er janvier. Les filtres
 * secondaires (statut, client…) vivent derrière l'icône, avec le nombre de
 * filtres actifs en pastille : rien de plus n'est visible en permanence.
 */
interface Props {
  recherche: string;
  onRecherche: (v: string) => void;
  placeholder: string;
  periode: PeriodeListe;
  onPeriode: (p: PeriodeListe) => void;
  customStart: string;
  customEnd: string;
  onCustom: (debut: string, fin: string) => void;
  /** Le nombre de filtres secondaires actifs — la pastille sur l'icône. */
  filtresActifs: number;
  onEffacerFiltres: () => void;
  /** Le contenu du panneau de filtres secondaires. */
  children: ReactNode;
}

export function BarreDeFiltres({
  recherche, onRecherche, placeholder, periode, onPeriode, customStart, customEnd, onCustom,
  filtresActifs, onEffacerFiltres, children,
}: Props) {
  const maintenant = new Date();
  return (
    <div className="flex flex-col sm:flex-row gap-2">
      <div className="relative flex-1 min-w-0">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="search"
          value={recherche}
          onChange={(e) => onRecherche(e.target.value)}
          placeholder={placeholder}
          aria-label={placeholder}
          className="glass-input h-10 w-full rounded-lg pl-10 pr-3 text-sm focus:ring-1 focus:ring-primary/20"
        />
      </div>

      <div className="flex gap-2">
        <Deroulant
          libelle={libelleDePeriode(periode, maintenant, customStart, customEnd)}
          largeur="min-w-[170px]"
        >
          {(fermer) => (
            <div className="p-1.5 space-y-0.5">
              {PERIODES_LISTE.map((p) => (
                <button
                  key={p.valeur}
                  type="button"
                  onClick={() => { onPeriode(p.valeur); if (p.valeur !== "custom") fermer(); }}
                  className={cn(
                    "flex w-full items-center rounded-lg px-3 py-2 text-left text-sm transition-colors hover:bg-black/5 dark:hover:bg-white/10",
                    periode === p.valeur ? "font-semibold text-primary" : "text-foreground"
                  )}
                >
                  {p.libelle(maintenant)}
                </button>
              ))}
              {periode === "custom" && (
                <div className="mt-1 border-t border-border/60 px-2 pt-2 pb-1 space-y-2">
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="w-6">Du</span>
                    <input type="date" value={customStart} onChange={(e) => onCustom(e.target.value, customEnd)} className="glass-input h-8 flex-1 rounded-md px-2 text-sm" />
                  </label>
                  <label className="flex items-center gap-2 text-xs text-muted-foreground">
                    <span className="w-6">au</span>
                    <input type="date" value={customEnd} onChange={(e) => onCustom(customStart, e.target.value)} className="glass-input h-8 flex-1 rounded-md px-2 text-sm" />
                  </label>
                </div>
              )}
            </div>
          )}
        </Deroulant>

        <Deroulant
          icone={<SlidersHorizontal className="h-4 w-4" />}
          libelle=""
          titre="Filtres"
          pastille={filtresActifs}
          largeur="w-10 justify-center px-0"
          alignerADroite
        >
          {(fermer) => (
            <div className="w-[280px] p-3 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Filtres</p>
                {filtresActifs > 0 && (
                  <button type="button" onClick={() => { onEffacerFiltres(); fermer(); }} className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
                    <X className="h-3 w-3" /> Effacer
                  </button>
                )}
              </div>
              {children}
            </div>
          )}
        </Deroulant>
      </div>
    </div>
  );
}

/** Un bouton qui ouvre un panneau, fermé au clic dehors ou sur Échap. */
export function Deroulant({ libelle, icone, titre, pastille, largeur, alignerADroite, children }: {
  libelle: string; icone?: ReactNode; titre?: string; pastille?: number; largeur?: string; alignerADroite?: boolean;
  children: (fermer: () => void) => ReactNode;
}) {
  const [ouvert, setOuvert] = useState(false);
  const racine = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!ouvert) return;
    const dehors = (e: MouseEvent) => { if (!racine.current?.contains(e.target as Node)) setOuvert(false); };
    const echap = (e: KeyboardEvent) => { if (e.key === "Escape") setOuvert(false); };
    document.addEventListener("mousedown", dehors);
    document.addEventListener("keydown", echap);
    return () => { document.removeEventListener("mousedown", dehors); document.removeEventListener("keydown", echap); };
  }, [ouvert]);

  return (
    <div ref={racine} className="relative">
      <button
        type="button"
        onClick={() => setOuvert((o) => !o)}
        title={titre}
        aria-label={titre ?? libelle}
        aria-expanded={ouvert}
        className={cn(
          "glass-input relative inline-flex h-10 items-center gap-2 rounded-lg px-3 text-sm transition-colors hover:border-primary/30",
          ouvert && "border-primary/40",
          largeur
        )}
      >
        {icone}
        {libelle && <span className="truncate">{libelle}</span>}
        {!icone && <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" />}
        {pastille ? (
          <span className="absolute -right-1.5 -top-1.5 inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-primary px-1 text-[11px] font-semibold text-primary-foreground">
            {pastille}
          </span>
        ) : null}
      </button>
      {ouvert && (
        <div className={cn(
          "glass-dropdown absolute z-40 mt-1.5 min-w-full rounded-xl border border-border/60 shadow-xl animate-in fade-in zoom-in-95 duration-100",
          alignerADroite ? "right-0" : "left-0"
        )}>
          {children(() => setOuvert(false))}
        </div>
      )}
    </div>
  );
}

/** Un champ du panneau de filtres. */
export function ChampFiltre({ libelle, children }: { libelle: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs text-muted-foreground">{libelle}</span>
      {children}
    </label>
  );
}

export const classeSelectFiltre = "glass-input h-9 w-full rounded-md px-2 text-sm";
